import React, { useEffect, useRef, useState } from 'react';
import { Megaphone, Plus, Search } from 'lucide-react';
import { api } from '../../App';
import toast from 'react-hot-toast';
import ConfirmationDialog from '../ConfirmationDialog';
import Button from '../Button';
import Chip, { ChipTone } from '../Chip';
import { DeleteIcon, EditIcon } from '../icons';
import ActionsHeaderHelp from '../ActionsHeaderHelp';
import FormModal from '@shared/ui/FormModal';
import { EMPTY_VALUE } from '../../utils/dateUtils';
import { ballotDesignation } from '../ballot-initiatives/initiativeDisplay';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell,
} from '../StandardTable';

/* Public positions manager (roadmap Phase 2 §6): the org's PUBLISHED stances
   on candidates / ballot measures / bills — what citizens who belong to or
   follow the org see as badges across their ballot and legislation feeds.
   Endpoints: GET/POST /organizations/:id/public-positions,
   PUT .../:positionId (edit), PUT .../:positionId/publish, DELETE .../:positionId.
   Publishing here is the explicit act (D-2/§6): private workspace positions are
   never exposed. */

type EntityType = 'candidate' | 'ballot_initiative' | 'bill';

interface PublicPosition {
  id: string;
  entity_type: EntityType;
  candidate_id?: string | null;
  ballot_initiative_id?: string | null;
  bill_id?: number | null;
  stance: string;
  statement?: string | null;
  source_url?: string | null;
  published: boolean;
  published_at?: string | null;
  updated_at?: string;
  entity_label?: string | null;
}

interface EntityOption {
  id: string;
  label: string;
  sub?: string;
}

const ENTITY_META: Record<EntityType, { label: string; stances: Array<{ id: string; label: string }> }> = {
  candidate: {
    label: 'Candidate',
    stances: [
      { id: 'endorse', label: 'Endorse' },
      { id: 'oppose', label: 'Oppose' },
    ],
  },
  ballot_initiative: {
    label: 'Ballot measure',
    stances: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
    ],
  },
  bill: {
    label: 'Bill',
    stances: [
      { id: 'support', label: 'Support' },
      { id: 'priority', label: 'Priority (pushing)' },
      { id: 'oppose', label: 'Oppose' },
    ],
  },
};

/** Stance → Chip tone. Support/oppose keep the green/red position grammar. */
const STANCE_TONE: Record<string, ChipTone> = {
  endorse: 'positive',
  support: 'positive',
  yes: 'positive',
  priority: 'brand',
  oppose: 'negative',
  no: 'negative',
};

/** The shared field style; see FRONTEND_DESIGN_CONVENTIONS.md (surfaces). */
const inputClass = 'input';

/** Statuses that put a measure in front of voters this election — qualified or
    on the ballot, not yet decided (passed/failed) and not pre-ballot. */
const ACTIVE_MEASURE_STATUSES = new Set(['qualified', 'on_ballot']);

/** "Amendment 81 — Graduated Income Tax": the designation voters know, then the
    common title. A measure without a designation yet falls back to its filing number. */
function measureOptionLabel(m: {
  measure_number?: string | null;
  sos_initiative_number?: string | null;
  popular_name?: string | null;
  unofficial_caption?: string | null;
  ballot_title?: string | null;
}): string {
  const designation = ballotDesignation(m.measure_number)
    || (m.sos_initiative_number ? `Initiative #${m.sos_initiative_number}` : null);
  const title = m.popular_name || m.unofficial_caption || m.ballot_title;
  return [designation, title].filter(Boolean).join(' — ') || 'Untitled measure';
}

/** Ballot measures are a short, closed list: the current election year's active
    measures, loaded once and filtered locally — never the whole filing history. */
function useActiveMeasures(enabled: boolean) {
  const [measures, setMeasures] = useState<EntityOption[]>([]);
  const [loading, setLoading] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    if (!enabled || loaded.current) return;
    loaded.current = true;
    setLoading(true);
    api
      .get('/ballot-initiatives', {
        params: {
          status_group: 'ballot_visible',
          election_year: new Date().getFullYear(),
          sortBy: 'ballot_order',
          sortDirection: 'asc',
          limit: 200,
        },
      })
      .then((res) => {
        const rows = res.data?.data?.initiatives ?? res.data?.data ?? [];
        setMeasures(
          rows
            .filter((m: any) => ACTIVE_MEASURE_STATUSES.has(m.status))
            .map((m: any) => ({ id: m.id, label: measureOptionLabel(m) }))
        );
      })
      .catch(() => {
        loaded.current = false;
        setMeasures([]);
      })
      .finally(() => setLoading(false));
  }, [enabled]);

  return { measures, loading };
}

/** Debounced entity search against the existing list endpoints (candidates, bills). */
function useEntitySearch(entityType: EntityType, query: string) {
  const [options, setOptions] = useState<EntityOption[]>([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (entityType === 'ballot_initiative' || query.trim().length < 2) {
      setOptions([]);
      return;
    }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        if (entityType === 'candidate') {
          const res = await api.get('/candidates', { params: { search: query.trim(), limit: 8 } });
          const rows = res.data?.data?.candidates ?? res.data?.data ?? [];
          setOptions(
            rows.map((c: any) => ({
              id: c.id,
              label: c.full_name || c.name || 'Unknown candidate',
              sub: [c.office_sought, c.party_affiliation, c.election_year].filter(Boolean).join(' · '),
            }))
          );
        } else {
          const res = await api.get('/bills', { params: { search: query.trim(), limit: 8 } });
          const rows = res.data?.data?.bills ?? [];
          setOptions(rows.map((b: any) => ({ id: String(b.id), label: `${b.billNumber} — ${b.title}` })));
        }
      } catch {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [entityType, query]);

  return { options, searching };
}

const PublicPositionsTab: React.FC<{ organizationId: string }> = ({ organizationId }) => {
  const [positions, setPositions] = useState<PublicPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<PublicPosition | null>(null);

  /* Edit dialog (#1161). The entity stays fixed — only the stance and the wording
     are editable — so the dialog holds a draft of those three fields alone. */
  const [editTarget, setEditTarget] = useState<PublicPosition | null>(null);
  const [editStance, setEditStance] = useState('');
  const [editStatement, setEditStatement] = useState('');
  const [editSourceUrl, setEditSourceUrl] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Add-position form
  const [entityType, setEntityType] = useState<EntityType>('candidate');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<EntityOption | null>(null);
  const [stance, setStance] = useState('endorse');
  const [statement, setStatement] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const isMeasure = entityType === 'ballot_initiative';
  const search = useEntitySearch(entityType, picked ? '' : query);
  const { measures, loading: measuresLoading } = useActiveMeasures(isMeasure);
  const needle = query.trim().toLowerCase();
  const options = isMeasure
    ? measures.filter((m) => !needle || m.label.toLowerCase().includes(needle))
    : search.options;
  const searching = isMeasure ? measuresLoading : search.searching;
  // Measures list on focus (a short, closed set); candidates and bills need a query.
  const showPicker = !picked && (isMeasure ? pickerOpen : query.trim().length >= 2 || searching);

  const fetchPositions = async () => {
    try {
      const res = await api.get(`/organizations/${organizationId}/public-positions`);
      if (res.data?.success) setPositions(res.data.data.positions ?? []);
    } catch (err) {
      console.error('Failed to fetch public positions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) fetchPositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const changeType = (t: EntityType) => {
    setEntityType(t);
    setStance(ENTITY_META[t].stances[0].id);
    setPicked(null);
    setQuery('');
  };

  const submit = async (published: boolean) => {
    if (!picked) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        entity_type: entityType,
        stance,
        statement: statement.trim() || null,
        source_url: sourceUrl.trim() || null,
        published,
      };
      if (entityType === 'candidate') body.candidate_id = picked.id;
      else if (entityType === 'ballot_initiative') body.ballot_initiative_id = picked.id;
      else body.bill_id = Number(picked.id);
      const res = await api.post(`/organizations/${organizationId}/public-positions`, body);
      if (res.data?.success) {
        toast.success(published ? 'Position published' : 'Position saved as draft');
        setPicked(null);
        setQuery('');
        setStatement('');
        setSourceUrl('');
        fetchPositions();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to save position');
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (p: PublicPosition) => {
    try {
      const res = await api.put(`/organizations/${organizationId}/public-positions/${p.id}/publish`, {
        published: !p.published,
      });
      if (res.data?.success) {
        setPositions((prev) => prev.map((x) => (x.id === p.id ? { ...x, ...res.data.data.position, entity_label: x.entity_label } : x)));
        toast.success(!p.published ? 'Published — visible to your members and followers' : 'Unpublished');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to update position');
    }
  };

  const openEdit = (p: PublicPosition) => {
    setEditTarget(p);
    setEditStance(p.stance);
    setEditStatement(p.statement || '');
    setEditSourceUrl(p.source_url || '');
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      const res = await api.put(`/organizations/${organizationId}/public-positions/${editTarget.id}`, {
        stance: editStance,
        statement: editStatement || null,
        source_url: editSourceUrl || null,
      });
      if (res.data?.success) {
        // Keep entity_label: it comes from a join the update does not return.
        setPositions((prev) =>
          prev.map((x) => (x.id === editTarget.id ? { ...x, ...res.data.data.position, entity_label: x.entity_label } : x))
        );
        toast.success('Position updated');
        setEditTarget(null);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to update position');
    } finally {
      setEditSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/organizations/${organizationId}/public-positions/${deleteTarget.id}`);
      setPositions((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      toast.success('Position deleted');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to delete position');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Add position */}
      <div className="card">
        <div className="flex items-center gap-2 mb-1">
          <Megaphone className="w-5 h-5 text-primary-700 dark:text-primary-400" />
          <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary">Take a public position</h3>
        </div>
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-5">
          Published positions appear as badges to citizens who belong to or follow your organization — on their
          ballot, in candidate comparisons, and on legislation. A published position on a candidate or ballot measure
          is also listed publicly with that candidate's or measure's endorsements. Drafts stay private to your admins.
        </p>

        <div className="grid sm:grid-cols-[160px_1fr_170px] gap-3 items-start">
          <select value={entityType} onChange={(e) => changeType(e.target.value as EntityType)} className={inputClass}>
            {(Object.keys(ENTITY_META) as EntityType[]).map((t) => (
              <option key={t} value={t}>
                {ENTITY_META[t].label}
              </option>
            ))}
          </select>

          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary dark:text-text-dark-tertiary" />
            <input
              value={picked ? picked.label : query}
              onChange={(e) => {
                setPicked(null);
                setQuery(e.target.value);
                setPickerOpen(true);
              }}
              onFocus={() => setPickerOpen(true)}
              onBlur={() => setPickerOpen(false)}
              placeholder={
                isMeasure
                  ? `Choose a ${new Date().getFullYear()} ballot measure…`
                  : `Search ${ENTITY_META[entityType].label.toLowerCase()}s…`
              }
              className={`${inputClass} pl-9`}
            />
            {showPicker && (
              /* mousedown would blur the input and close the list before the click lands */
              <div
                onMouseDown={(e) => e.preventDefault()}
                className="absolute z-20 mt-1 w-full rounded-lg border border-border-light dark:border-border-dark-hairline bg-white dark:bg-background-dark-secondary shadow-lg max-h-64 overflow-y-auto"
              >
                {searching && (
                  <div className="px-3 py-2 text-sm text-text-tertiary dark:text-text-dark-tertiary">
                    {isMeasure ? 'Loading…' : 'Searching…'}
                  </div>
                )}
                {!searching && options.length === 0 && (
                  <div className="px-3 py-2 text-sm text-text-tertiary dark:text-text-dark-tertiary">
                    {isMeasure && !needle
                      ? `No measures have qualified for the ${new Date().getFullYear()} ballot yet`
                      : 'No matches'}
                  </div>
                )}
                {options.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => {
                      setPicked(o);
                      setPickerOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary"
                  >
                    <div className="text-sm text-text-primary dark:text-text-dark-primary">{o.label}</div>
                    {o.sub && <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{o.sub}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <select value={stance} onChange={(e) => setStance(e.target.value)} className={inputClass}>
            {ENTITY_META[entityType].stances.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div className="grid sm:grid-cols-[1fr_260px] gap-3 mt-3">
          <input
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
            maxLength={4000}
            placeholder="Statement (optional) — why your organization takes this position"
            className={inputClass}
          />
          <input
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            placeholder="Source link (optional)"
            className={inputClass}
          />
        </div>

        <div className="flex items-center justify-end gap-2 mt-4">
          <Button variant="secondary" size="md" disabled={!picked || saving} onClick={() => submit(false)}>
            Save draft
          </Button>
          <Button variant="primary" size="md" icon={Plus} loading={saving} disabled={!picked} onClick={() => submit(true)}>
            Save & publish
          </Button>
        </div>
      </div>

      {/* Existing positions */}
      <div className="card">
        <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary mb-4">
          Your positions {positions.length > 0 && <span className="text-text-tertiary dark:text-text-dark-tertiary font-normal">· {positions.length}</span>}
        </h3>
        {loading && <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading…</p>}
        {!loading && positions.length === 0 && (
          <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
            No public positions yet — take one above and publish it when you're ready.
          </p>
        )}
        {!loading && positions.length > 0 && (
          <StandardTable>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell columnType="fit">Position</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit">Type</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="auto">Item</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="expand">Statement</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit">Status</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="actions" align="right">
                  <span className="inline-flex items-center gap-1.5">
                    Actions
                    <ActionsHeaderHelp icons={['edit', 'delete']} />
                  </span>
                </StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {positions.map((p) => (
                <StandardTableRow key={p.id} hoverable>
                  <StandardTableCell>
                    <Chip label={p.stance} tone={STANCE_TONE[p.stance] || 'neutral'} size="sm" />
                  </StandardTableCell>
                  <StandardTableCell>
                    <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                      {ENTITY_META[p.entity_type]?.label}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell>
                    <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary break-words">
                      {p.entity_label || p.candidate_id || p.ballot_initiative_id || p.bill_id}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell>
                    {p.statement ? (
                      <span className="text-sm text-text-secondary dark:text-text-dark-secondary break-words line-clamp-2">
                        {p.statement}
                      </span>
                    ) : (
                      <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">{EMPTY_VALUE}</span>
                    )}
                  </StandardTableCell>
                  <StandardTableCell>
                    {/* The status chip is the publish toggle — clicking it flips
                        the position between Published and Draft. */}
                    <button
                      type="button"
                      role="switch"
                      aria-checked={p.published}
                      onClick={() => togglePublished(p)}
                      title={p.published ? 'Unpublish this position' : 'Publish this position'}
                    >
                      <Chip
                        label={p.published ? 'Published' : 'Draft'}
                        tone={p.published ? 'positive' : 'neutral'}
                        size="sm"
                      />
                    </button>
                  </StandardTableCell>
                  <StandardTableCell align="right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        aria-label={`Edit position on ${p.entity_label || 'this item'}`}
                        title={`Edit position on ${p.entity_label || 'this item'}`}
                        onClick={() => openEdit(p)}
                        className="p-1.5 rounded text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                      >
                        <EditIcon className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Delete position on ${p.entity_label || 'this item'}`}
                        title={`Delete position on ${p.entity_label || 'this item'}`}
                        onClick={() => setDeleteTarget(p)}
                        className="p-1.5 rounded text-text-tertiary dark:text-text-dark-tertiary hover:text-error-600 dark:hover:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors"
                      >
                        <DeleteIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </StandardTableCell>
                </StandardTableRow>
              ))}
            </StandardTableBody>
          </StandardTable>
        )}
      </div>

      <FormModal
        isOpen={Boolean(editTarget)}
        title="Edit public position"
        description={editTarget?.entity_label || undefined}
        onClose={() => setEditTarget(null)}
        onSubmit={saveEdit}
        submitLabel="Save changes"
        isSubmitting={editSaving}
        isDirty={
          Boolean(editTarget) &&
          (editStance !== editTarget?.stance ||
            editStatement !== (editTarget?.statement || '') ||
            editSourceUrl !== (editTarget?.source_url || ''))
        }
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="edit-position-stance" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
              Position
            </label>
            <select
              id="edit-position-stance"
              value={editStance}
              onChange={(e) => setEditStance(e.target.value)}
              className={inputClass}
            >
              {/* Stance vocabulary is per entity type, and the entity cannot change here. */}
              {(editTarget ? ENTITY_META[editTarget.entity_type].stances : []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="edit-position-statement" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
              Statement
            </label>
            <input
              id="edit-position-statement"
              value={editStatement}
              onChange={(e) => setEditStatement(e.target.value)}
              maxLength={4000}
              placeholder="Why your organization takes this position"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="edit-position-source" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
              Source link
            </label>
            <input
              id="edit-position-source"
              value={editSourceUrl}
              onChange={(e) => setEditSourceUrl(e.target.value)}
              placeholder="https://…"
              className={inputClass}
            />
          </div>
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            Publishing is handled by the status chip in the table — this dialog changes the wording only.
          </p>
        </div>
      </FormModal>

      <ConfirmationDialog
        isOpen={Boolean(deleteTarget)}
        title="Delete public position?"
        message={`This removes your organization's ${deleteTarget?.stance || ''} position on "${deleteTarget?.entity_label || 'this item'}". Citizens will no longer see the badge.`}
        confirmText="Delete"
        onConfirm={remove}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default PublicPositionsTab;
